/*
 * Copyright (c) 2023 - 2026. Bluemate B.V.
 */

import React, {PropsWithChildren} from 'react';
import currencyEuro from '@iconify/icons-bi/currency-euro';
import envelopeFill from '@iconify/icons-bi/envelope-fill';
import starIcon from '@iconify/icons-bi/star-fill';
import usersCog from '@iconify/icons-fa-solid/users-cog';
import chartIcon from '@iconify/icons-fluent-mdl2/chart';
import cogIcon from '@iconify/icons-ion/cog';
import tileIcon from '@iconify/icons-ls/tile';
import Bell from '@iconify/icons-mdi/bell-outline';
import {Icon} from '@iconify/react';
import {AwesomeIcon} from '@dvrd/dvr-controls';
import {Navigate, Outlet, RouteObject} from 'react-router';
import App from './app';
import {BluemateBadge} from './components/controls';
import CreateAccountController from './components/account/create/createAccountController';
import AccountDetailsController from './components/admin/account/accountDetailsController';
import AdminController from './components/admin/adminController';
import DefaultEmailsController from './components/admin/defaultEmails/defaultEmailsController';
import DefaultEmailEditorController from './components/admin/defaultEmails/editor/defaultEmailEditorController';
import AdminEmailSequenceController from './components/admin/emailSequence/adminEmailSequenceController';
import FailedPaymentsController from './components/admin/failedPayments/failedPaymentsController';
import HistoryImportController from './components/admin/historyImport/historyImportController';
import AdminNavigation from './components/admin/navigation/adminNavigation';
import SystemServicesController from './components/admin/systemService/systemServicesController';
import ThreadsController from './components/admin/threads/threadsController';
import AdminUsersController from './components/admin/users/adminUsersController';
import UserDetailsController from './components/admin/users/details/userDetailsController';
import UserSelectionController from './components/admin/userSelection/userSelectionController';
import PlatformUsageController from './components/admin/platformUsage/platformUsageController';
import LoginController from './components/auth/login/loginController';
import ForgotPasswordController from './components/auth/password/forgot/forgotPasswordController';
import ResetPasswordController from './components/auth/password/reset/resetPasswordController';
import RegisterController from './components/auth/register/registerController';
import ConnectionCompleteController from './components/connectionComplete/connectionCompleteController';
import DashboardController from './components/dashboard/dashboardController';
import EmailFlowController from './components/email/emailFlowController';
import TemplateEditorController from './components/email/template/editor/templateEditorController';
import UnsubscribeMonthly from './components/email/unsubscribe/unsubscribeMonthly';
import CreditInvoiceDataController from './components/invoice/credit/creditInvoiceDataController';
import InvoiceDetailsController from './components/invoice/details/invoiceDetailsController';
import EditInvoiceController from './components/invoice/edit/editInvoiceController';
import PeppolInvoicesController from './components/invoice/peppol/peppolInvoicesController';
import Maintenance from './components/maintenance/maintenance';
import AppRoute from './components/misc/appRoute';
import NotificationsPageController from './components/notifications/notificationsPageController';
import BolOauthController from './components/oauth/bol/bolOauthController';
import OAuthAuthorizeController from './components/oauth/authorize/oauthAuthorizeController';
import PdfTemplateController from './components/pdfTemplate/create/pdfTemplateController';
import PriceCalculator from './components/priceSlider/priceCalculator';
import ProductsController from './components/products/productsController';
import MobileAppController from './components/mobileApp/mobileAppController';
import ChromeExtensionController from './components/chromeExtension/chromeExtensionController';
import McpServerController from './components/mcpServer/mcpServerController';
import DashboardPageController from './components/dashboardPage/dashboardPageController';
import ReferAFriendController from './components/referAFriend/referAFriendController';
import ReturnsController from './components/returns/returnsController';
import ProductReviewsController from './components/reviewAnalytics/productReviews/productReviewsController';
import ReviewDashboardController from './components/reviewAnalytics/reviewAnalyticsController';
import ReviewAnalyticsRoute from './components/reviewAnalytics/reviewAnalyticsRoute';
import SalesDashboardController from './components/salesDashboard/salesDashboardController';
import SalesDashboardControllerV2 from './components/salesDashboardV2/salesDashboardController';
import SearchRankingsController from './components/searchRankings/searchRankingsController';
import SearchVolumeController from './components/searchVolume/searchVolumeController';
import AccountUsersController from './components/settings/accountUsers/accountUsersController';
import ApiSettingsController from './components/settings/api/apiSettingsController';
import CommonSettingsController from './components/settings/common/commonSettingsController';
import EmailSettingsController from './components/settings/email/emailSettingsController';
import ExportInvoicesController from './components/settings/export/exportInvoicesController';
import InvoiceSettingsController from './components/settings/invoice/invoiceSettingsController';
import PasswordSettingsController from './components/settings/password/passwordSettingsController';
import Settings from './components/settings/settings';
import SubscriptionSettingsController from './components/settings/subscription/subscriptionSettingsController';
import ProductsMovedController from './components/settings/productsMoved/productsMovedController';
import ConfirmIdeal from './components/subscription/ideal/confirmIdeal';
import IdealController from './components/subscription/ideal/idealController';
import ResetTwoFactorController from './components/twoFactor/reset/resetTwoFactorController';
import VatDeclarationController from './components/vatDeclaration/vatDeclarationController';
import Account from './models/accountModel';
import User from './models/userModel';
import {userHasRight} from './util/accessUtil';
import {
    ACCESS_SETTINGS_PATH,
    ACCESS_SETTINGS_ROUTE,
    ADMIN_ACCOUNT_PATH,
    ADMIN_DEFAULT_TEMPLATES_PATH,
    ADMIN_DEFAULT_TEMPLATES_ROUTE,
    ADMIN_EMAILS_PATH,
    ADMIN_EMAILS_ROUTE,
    ADMIN_IMPORT_PATH,
    ADMIN_IMPORT_ROUTE,
    ADMIN_PATH,
    ADMIN_PAYMENTS_PATH,
    ADMIN_PAYMENTS_ROUTE,
    ADMIN_PLATFORM_USAGE_PATH,
    ADMIN_PLATFORM_USAGE_ROUTE,
    ADMIN_ROUTE,
    ADMIN_SERVICES_PATH,
    ADMIN_SERVICES_ROUTE,
    ADMIN_TEMPLATE_EDITOR_PATH,
    ADMIN_THREADS_PATH,
    ADMIN_THREADS_ROUTE,
    ADMIN_USER_PATH,
    ADMIN_USERS_PATH,
    ADMIN_USERS_ROUTE,
    ADMIN_USERS_SELECT_PATH,
    API_SETTINGS_PATH,
    API_SETTINGS_ROUTE,
    CHROME_EXTENSION_PATH,
    CHROME_EXTENSION_ROUTE,
    COMMON_SETTINGS_PATH,
    COMMON_SETTINGS_ROUTE,
    CONNECTION_COMPLETE_PATH,
    CREDIT_DATA_PATH,
    DASHBOARD_PATH,
    DASHBOARD_ROUTE,
    DEMO_PATH,
    EDIT_INVOICE_PATH,
    EMAIL_SEQUENCE_PATH,
    EMAIL_SETTINGS_PATH,
    EMAIL_SETTINGS_ROUTE,
    EMAIL_TEMPLATE_PATH,
    EMAILS_PATH,
    EMAILS_ROUTE,
    EXPORT_PATH,
    EXPORT_ROUTE,
    FORGOT_PASSWORD_PATH,
    IDEAL_COMPLETE_PATH,
    IDEAL_PATH,
    INVOICE_DETAILS_PATH,
    INVOICE_REQUESTS_PATH,
    INVOICE_REQUESTS_ROUTE,
    INVOICE_SETTINGS_PATH,
    INVOICE_SETTINGS_ROUTE,
    INVOICES_PATH,
    LOGIN_PATH,
    LOGIN_ROUTE,
    MAINTENANCE_PATH,
    MCP_SERVER_PATH,
    MCP_SERVER_ROUTE,
    MOBILE_APP_PATH,
    MOBILE_APP_ROUTE,
    NOTIFICATIONS_PATH,
    NOTIFICATIONS_ROUTE,
    OAUTH_AUTHORIZE_PATH,
    OAUTH_INSIGHTS_PATH,
    OAUTH_PATH,
    ORDERS_PATH,
    ORDERS_ROUTE,
    PDF_LAYOUT_PATH,
    PDF_LAYOUT_ROUTE,
    PEPPOL_INVOICES_PATH,
    PEPPOL_INVOICES_ROUTE,
    PRICE_CALCULATOR_PATH,
    PRODUCT_SETTINGS_PATH,
    PRODUCTS_PATH,
    PRODUCTS_ROUTE,
    QR_CODES_PATH,
    REFER_A_FRIEND_PATH,
    REFER_A_FRIEND_ROUTE,
    REGISTER_PATH,
    REGISTER_ROUTE,
    RESET_PASSWORD_PATH,
    RETURNS_PATH,
    RETURNS_ROUTE,
    REVIEW_ANALYTICS_PATH,
    REVIEW_ANALYTICS_ROUTE,
    SALES_DASHBOARD_PATH,
    SALES_DASHBOARD_V2_PATH,
    SALES_DASHBOARD_V2_ROUTE,
    SEARCH_RANKINGS_PATH,
    SEARCH_RANKINGS_ROUTE,
    SEARCH_TRENDS_PATH,
    SEARCH_VOLUMES_PATH,
    SEARCH_VOLUMES_ROUTE,
    SECURITY_SETTINGS_PATH,
    SECURITY_SETTINGS_ROUTE,
    SETTINGS_PATH,
    SETTINGS_ROUTE,
    SUBSCRIPTION_PATH,
    SUBSCRIPTION_ROUTE,
    TWO_FACTOR_RESET_PATH,
    UNSUBSCRIBE_MONTHLY_PATH,
    VAT_DECLARATION_PATH,
    VAT_DECLARATION_ROUTE
} from './util/constants';
import {onChangeDashboardTab} from './util/events';
import {AccessKey, BluemateSidebarItem} from './util/interfaces';
import {hasAdminLogin} from './util/adminUtil';
import i18n from './i18n';
import {BluemateBadgeColor, BluemateBadgeSize} from './components/controls/badge/bluemateBadge.tsx';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function adminAuthenticator(user: User | null) {
    return user?.isAdmin() || hasAdminLogin();
}

interface SubTextProps extends PropsWithChildren {
    isNew?: boolean;
    badgeLabel?: string;
    badgeTooltip?: string;
    badgeColor?: BluemateBadgeColor;
    badgeSize?: BluemateBadgeSize;
}

function SubText(props: SubTextProps) {
    const {isNew, badgeLabel, badgeTooltip, badgeColor = 'blue', badgeSize = 'small', children} = props;
    return (
        <span className='sub-label' style={{display: 'flex', alignItems: 'center', gap: '.25rem'}}>
            {isNew && i18n.t('menu.new', 'Nieuw')}
            {!!badgeLabel && (
                <>
                    &nbsp;
                    <BluemateBadge label={badgeLabel} color={badgeColor} size={badgeSize} tooltip={badgeTooltip} />
                </>
            )}
            {children}
        </span>
    );
}

export const getSidebarItems = (user: User | null, account: Account | null): Array<BluemateSidebarItem> => {
    const items: Array<BluemateSidebarItem> = user
        ? [
              {
                  index: 0,
                  label: i18n.t('menu.home', 'Overzicht'),
                  icon: <Icon icon='lucide:house' />,
                  id: 'home',
                  route: DASHBOARD_ROUTE,
                  accessKey: AccessKey.PAGE_DASHBOARD
              },
              {
                  index: 1,
                  label: i18n.t('menu.invoices', 'Facturen'),
                  icon: <Icon icon={tileIcon} className='icon-sm' />,
                  id: 'dashboard',
                  route: ORDERS_ROUTE,
                  alwaysOpen: false,
                  accessKey: [
                      AccessKey.PAGE_ORDERS,
                      AccessKey.PAGE_INVOICE_REQUESTS,
                      AccessKey.PAGE_RETURNS,
                      AccessKey.PAGE_INVOICE_LAYOUT
                  ],
                  children: [
                      {
                          index: 0,
                          label: i18n.t('menu.orders', 'Orders'),
                          id: 'orders',
                          route: ORDERS_ROUTE,
                          onClick: onChangeDashboardTab,
                          accessKey: AccessKey.PAGE_ORDERS
                      },
                      {
                          index: 0,
                          label: i18n.t('menu.requests', 'Aanvragen'),
                          id: 'invoice-requests',
                          route: INVOICE_REQUESTS_ROUTE,
                          onClick: onChangeDashboardTab,
                          accessKey: AccessKey.PAGE_INVOICE_REQUESTS
                      },
                      {
                          index: 0,
                          label: i18n.t('menu.peppol', 'Peppol'),
                          id: 'peppol-invoices',
                          route: PEPPOL_INVOICES_ROUTE,
                          accessKey: AccessKey.PAGE_ORDERS,
                          authenticator: (_: User | null, account: Account | null) => {
                              if (!account) return false;
                              if (account.vatNumber.startsWith('BE')) return true;
                              for (const vatNumber of Object.values(account.vatNumbers))
                                  if (vatNumber.startsWith('BE')) return true;
                              return false;
                          }
                      },
                      {
                          index: 0,
                          label: i18n.t('menu.returns', 'Retouren'),
                          id: 'returns',
                          route: RETURNS_ROUTE,
                          accessKey: AccessKey.PAGE_RETURNS
                      },
                      {
                          index: 0,
                          label: i18n.t('menu.layout', 'Layout'),
                          id: 'pdf',
                          route: PDF_LAYOUT_ROUTE,
                          accessKey: AccessKey.PAGE_INVOICE_LAYOUT
                      }
                  ]
              },
              {
                  index: 2,
                  label: i18n.t('menu.emails', 'E-mails'),
                  icon: <Icon icon={envelopeFill} className='icon-sm' />,
                  id: 'mail-template',
                  route: EMAILS_ROUTE,
                  accessKey: AccessKey.PAGE_EMAIL
              },
              {
                  index: 8,
                  label: i18n.t('menu.notifications', 'Notificaties'),
                  icon: <Icon icon={Bell} className='icon-sm' />,
                  id: 'notifications',
                  route: NOTIFICATIONS_ROUTE,
                  accessKey: AccessKey.PAGE_NOTIFICATIONS
              },
              {
                  index: 6,
                  label: i18n.t('menu.reviewAnalytics', 'Review Inzicht'),
                  icon: <Icon icon={starIcon} />,
                  id: 'review-analytics',
                  route: REVIEW_ANALYTICS_ROUTE,
                  accessKey: [
                      AccessKey.PAGE_REVIEWS_PRODUCTS,
                      AccessKey.PAGE_REVIEWS_FEED,
                      AccessKey.PAGE_REVIEWS_SETTINGS,
                      AccessKey.PAGE_REVIEWS_EXPORT
                  ]
              },
              {
                  index: 9,
                  label: i18n.t('menu.products', 'Producten'),

                  icon: <AwesomeIcon name='qrcode' />,
                  id: 'products',
                  route: PRODUCTS_ROUTE,
                  accessKey: AccessKey.PAGE_PRODUCTS
              },
              {
                  index: 5,
                  label: i18n.t('menu.searchVolume', 'Zoekvolume'),
                  icon: <Icon icon='pepicons-pop:loop' />,
                  id: 'search-term-dashboard',
                  route: SEARCH_VOLUMES_ROUTE,
                  accessKey: AccessKey.PAGE_SEARCH_TERMS
              },
              {
                  index: 12,
                  label: i18n.t('menu.settings', 'Instellingen'),
                  icon: <Icon icon={cogIcon} />,
                  id: 'settings',
                  route: SETTINGS_ROUTE,
                  alwaysOpen: false,
                  accessKey: 'allow',
                  children: [
                      {
                          index: 0,
                          label: i18n.t('menu.common', 'Algemeen'),
                          id: 'commonSettings',
                          route: COMMON_SETTINGS_ROUTE,
                          accessKey: 'allow'
                      },
                      {
                          index: 0,
                          label: i18n.t('menu.users', 'Gebruikers'),
                          id: 'accountUsers',
                          route: ACCESS_SETTINGS_ROUTE,
                          accessKey: AccessKey.PAGE_ACCOUNT_USERS
                      },
                      {
                          index: 0,
                          label: i18n.t('menu.security', 'Beveiliging'),
                          id: 'securitySettings',
                          route: SECURITY_SETTINGS_ROUTE,
                          accessKey: 'allow'
                      },
                      {
                          index: 0,
                          label: i18n.t('menu.apiConnection', 'Koppeling'),
                          id: 'apiSettings',
                          route: API_SETTINGS_ROUTE,
                          accessKey: AccessKey.PAGE_SETTINGS_API
                      },
                      {
                          index: 0,
                          label: i18n.t('menu.invoices', 'Facturen'),
                          id: 'invoiceSettings',
                          route: INVOICE_SETTINGS_ROUTE,
                          accessKey: AccessKey.PAGE_SETTINGS_INVOICE
                      },
                      {
                          index: 0,
                          label: i18n.t('menu.emails', 'E-mails'),
                          id: 'emailSettings',
                          route: EMAIL_SETTINGS_ROUTE,
                          accessKey: AccessKey.PAGE_SETTINGS_EMAILS
                      },
                      {
                          index: 0,
                          label: i18n.t('menu.export', 'Exporteren'),
                          id: 'exportSettings',
                          route: EXPORT_ROUTE,
                          accessKey: AccessKey.PAGE_SETTINGS_EXPORT
                      },
                      {
                          index: 0,
                          label: i18n.t('menu.subscription', 'Abonnement'),
                          id: 'subscriptionSettings',
                          route: SUBSCRIPTION_ROUTE,
                          accessKey: AccessKey.PAGE_SETTINGS_SUBSCRIPTION
                      }
                  ]
              },
              {
                  index: 13,
                  label: i18n.t('menu.referAFriend', 'Refer a friend'),
                  subText: '\u20ac50',
                  icon: <Icon icon='mingcute:gift-fill' />,
                  id: 'refer-a-friend',
                  route: REFER_A_FRIEND_ROUTE,
                  accessKey: 'allow'
              },
              {
                  index: 10,
                  label: i18n.t('menu.mobileApp', 'Mobiele app'),
                  icon: <Icon icon='mdi:cellphone' />,
                  id: 'mobile-app',
                  route: MOBILE_APP_ROUTE,
                  accessKey: 'allow'
              },
              {
                  index: 11,
                  label: i18n.t('menu.chromeExtension', 'Chrome Extensie'),
                  icon: <Icon icon='mdi:google-chrome' />,
                  id: 'chrome-extension',
                  route: CHROME_EXTENSION_ROUTE,
                  accessKey: 'allow'
              },
              {
                  index: 11.5,
                  label: i18n.t('menu.mcpServer', 'Agent MCP'),
                  subText: <SubText isNew />,
                  icon: <Icon icon='mdi:robot-outline' />,
                  id: 'mcp-server',
                  route: MCP_SERVER_ROUTE,
                  accessKey: 'allow'
              }
          ]
        : [
              {
                  label: i18n.t('menu.login', 'Inloggen'),
                  icon: 'lock',
                  id: 'login',
                  route: [LOGIN_ROUTE, REGISTER_ROUTE],
                  index: 0,
                  accessKey: 'allow'
              }
          ];
    if (user) {
        if (allowRevenueRoutes(account)) {
            items.push(
                {
                    index: 3,
                    label: i18n.t('menu.salesDashboard', 'Sales Dashboard'),
                    icon: <Icon icon={chartIcon} />,
                    id: 'sales-dashboard-v2',
                    route: SALES_DASHBOARD_V2_ROUTE,
                    accessKey: AccessKey.PAGE_SALES_DASHBOARD
                },
                {
                    index: 4,
                    label: i18n.t('menu.searchRankings', 'Search Rankings'),
                    icon: <AwesomeIcon name='magnifying-glass-chart' />,
                    id: 'search-rankings',
                    route: SEARCH_RANKINGS_ROUTE,
                    accessKey: AccessKey.PAGE_SEARCH_RANKINGS
                },
                {
                    index: 7,
                    label: i18n.t('menu.vatDeclaration', 'BTW Aangifte'),
                    icon: <Icon icon={currencyEuro} className='icon-sm' />,
                    id: 'vat-declaration',
                    route: VAT_DECLARATION_ROUTE,
                    accessKey: AccessKey.PAGE_VAT_DECLARATION
                }
            );
        }
        if (user.isSupport())
            items.push({
                index: 7,
                label: i18n.t('menu.admin', 'Admin'),
                icon: <Icon icon={usersCog} />,
                id: 'admin',
                route: ADMIN_ROUTE,
                exactRoute: false,
                alwaysOpen: false,
                accessKey: 'deny',
                children: [
                    {
                        index: 1,
                        label: i18n.t('menu.users', 'Gebruikers'),
                        id: 'adminUsers',
                        route: ADMIN_USERS_ROUTE,
                        accessKey: 'deny'
                    },
                    {
                        index: 2,
                        label: i18n.t('menu.threads', 'Threads'),
                        id: 'adminThreads',
                        route: ADMIN_THREADS_ROUTE,
                        accessKey: 'deny'
                    },
                    ...(user.isSuperAdmin()
                        ? [
                              {
                                  index: 3,
                                  label: i18n.t('menu.cron', 'Cron'),
                                  id: 'adminServices',
                                  route: ADMIN_SERVICES_ROUTE,
                                  accessKey: 'deny' as const
                              },
                              {
                                  index: 4,
                                  label: i18n.t('menu.thirtyDayImport', '30 dagen import'),
                                  id: 'adminImport',
                                  route: ADMIN_IMPORT_ROUTE,
                                  accessKey: 'deny' as const
                              },
                              {
                                  index: 5,
                                  label: i18n.t('menu.sendEmails', 'E-mails verzenden'),
                                  id: 'adminEmails',
                                  route: ADMIN_EMAILS_ROUTE,
                                  accessKey: 'deny' as const
                              },
                              {
                                  index: 6,
                                  label: i18n.t('menu.failedPayments', 'Mislukte betalingen'),
                                  id: 'adminPayments',
                                  route: ADMIN_PAYMENTS_ROUTE,
                                  accessKey: 'deny' as const
                              },
                              {
                                  index: 7,
                                  label: i18n.t('menu.defaultTemplates', 'Standaard templates'),
                                  id: 'adminDefaultTemplates',
                                  route: ADMIN_DEFAULT_TEMPLATES_ROUTE,
                                  accessKey: 'deny' as const
                              },
                              {
                                  index: 8,
                                  label: i18n.t('menu.platformUsage', 'Platformgebruik'),
                                  id: 'adminPlatformUsage',
                                  route: ADMIN_PLATFORM_USAGE_ROUTE,
                                  accessKey: 'deny' as const
                              }
                          ]
                        : [])
                ]
            });
    }
    for (const parent of items) {
        if (!parent.children) continue;
        for (const child of parent.children) child.parentId = parent.id;
    }
    items.sort((itemA: BluemateSidebarItem, itemB: BluemateSidebarItem) => itemA.index - itemB.index);
    if (account && user) return filterAllowedItems(items, account, user);
    return items;
};

function filterAllowedItems(
    items: Array<BluemateSidebarItem>,
    account: Account,
    user: User
): Array<BluemateSidebarItem> {
    const filtered: Array<BluemateSidebarItem> = [];
    for (const item of items) {
        if (item.accessKey === 'deny') {
            if (!user.isSupport()) continue;
        } // Only support allowed
        else if (Array.isArray(item.accessKey)) {
            if (!item.accessKey.find((key: AccessKey) => userHasRight(account, key))) continue; // Item not allowed
        } else if (item.accessKey !== 'allow' && !userHasRight(account, item.accessKey)) continue;
        else if (item.authenticator && !item.authenticator(user, account)) continue;

        const cloned = {...item};
        if (cloned.children) cloned.children = filterAllowedItems(cloned.children, account, user);
        filtered.push(cloned);
    }
    return filtered;
}

function allowRevenueRoutes(account: Account | null): boolean {
    // Some users request that financial data is not visible in their account
    return account?.getFlex('hide_revenue') != true;
}

// Upper bound on a `?redirect=` value. Long enough for the OAuth authorize query
// (client_id + redirect_uri + PKCE challenge + state), short enough that the login
// round-trip cannot be used to carry an arbitrary payload.
const MAX_REDIRECT_LENGTH = 2048;

function hasUnsafeRedirectChars(redirect: string): boolean {
    for (const char of redirect) {
        const code = char.charCodeAt(0);
        if (code < 0x20 || code === 0x7f || char === '\\') return true;
    }
    return false;
}

/** The single validator for every `?redirect=` value, used both when the user
 *  arrives logged out (loginController) and when they are already logged in
 *  (app.tsx). On top of the route-table match it enforces what `isValidPath`
 *  cannot see: the value must be an absolute same-origin path (never
 *  protocol-relative), within the length bound, and free of control characters
 *  and backslashes — in the query string too, which `isValidPath` strips before
 *  matching. */
export function isSafeRedirectPath(redirect: string | null | undefined): boolean {
    if (!redirect || redirect.length > MAX_REDIRECT_LENGTH) return false;
    if (!redirect.startsWith('/') || redirect.startsWith('//')) return false;
    if (hasUnsafeRedirectChars(redirect)) return false;
    return isValidPath(redirect);
}

export function isValidPath(path: string, routes?: Array<RouteObject>): boolean {
    if (!routes) routes = Routes;
    if (path.startsWith('/')) path = path.substring(1);
    if (path.includes('?')) path = path.substring(0, path.indexOf('?'));
    // Reject path-traversal / backslash tricks outright.
    if (path.includes('..') || path.includes('\\')) return false;
    return routes.some((rout: RouteObject) => {
        // Match only an exact route or a proper sub-path of it — NOT the reverse
        // (a short attacker prefix of a real route, e.g. "/n" for "notificaties").
        if (rout.path && (path === rout.path || path.startsWith(rout.path + '/'))) return true;
        else if (rout.children && isValidPath(path, rout.children)) return true;
        return false;
    });
}

export const Routes: Array<RouteObject> = [
    {
        path: '*',
        element: <App />,
        children: [
            {
                path: REGISTER_PATH,
                element: (
                    <AppRoute>
                        <RegisterController />
                    </AppRoute>
                )
            },
            {
                path: LOGIN_PATH,
                element: (
                    <AppRoute>
                        <LoginController />
                    </AppRoute>
                )
            },
            {path: DEMO_PATH, element: <Navigate to='/inloggen?demo=1' />},
            {
                path: FORGOT_PASSWORD_PATH,
                element: (
                    <AppRoute>
                        <ForgotPasswordController />
                    </AppRoute>
                )
            },
            {
                path: RESET_PASSWORD_PATH,
                element: (
                    <AppRoute>
                        <ResetPasswordController />
                    </AppRoute>
                )
            },
            {
                path: TWO_FACTOR_RESET_PATH,
                element: (
                    <AppRoute>
                        <ResetTwoFactorController />
                    </AppRoute>
                )
            },
            {path: PRICE_CALCULATOR_PATH, element: <PriceCalculator />},
            {path: MAINTENANCE_PATH, element: <Maintenance />},
            {
                path: INVOICES_PATH,
                element: <Outlet />,
                children: [
                    {
                        path: ORDERS_PATH,
                        element: (
                            <AppRoute requireAccessKey={AccessKey.PAGE_ORDERS} authenticated>
                                <DashboardController />
                            </AppRoute>
                        )
                    },
                    {
                        path: INVOICE_REQUESTS_PATH,
                        element: (
                            <AppRoute requireAccessKey={AccessKey.PAGE_INVOICE_REQUESTS} authenticated>
                                <DashboardController />
                            </AppRoute>
                        )
                    },
                    {
                        path: PEPPOL_INVOICES_PATH,
                        element: (
                            <AppRoute requireAccessKey={AccessKey.PAGE_ORDERS} authenticated>
                                <PeppolInvoicesController />
                            </AppRoute>
                        )
                    },
                    {path: '*', element: <Navigate to={ORDERS_ROUTE} />}
                ]
            },
            {
                path: INVOICE_DETAILS_PATH,
                element: (
                    <AppRoute requireAccessKey={AccessKey.PAGE_ORDERS} authenticated>
                        <InvoiceDetailsController />
                    </AppRoute>
                )
            },
            {
                path: EDIT_INVOICE_PATH,
                element: (
                    <AppRoute requireAccessKey={AccessKey.PAGE_ORDERS} authenticated>
                        <EditInvoiceController />
                    </AppRoute>
                )
            },
            {
                path: PDF_LAYOUT_PATH,
                element: (
                    <AppRoute requireAccessKey={AccessKey.PAGE_INVOICE_LAYOUT} authenticated>
                        <PdfTemplateController />
                    </AppRoute>
                )
            },
            {
                path: CONNECTION_COMPLETE_PATH,
                element: (
                    <AppRoute authenticated>
                        <ConnectionCompleteController />
                    </AppRoute>
                )
            },
            {
                path: REVIEW_ANALYTICS_PATH,
                element: (
                    <AppRoute
                        requireAccessKey={[
                            AccessKey.PAGE_REVIEWS_PRODUCTS,
                            AccessKey.PAGE_REVIEWS_FEED,
                            AccessKey.PAGE_REVIEWS_SETTINGS,
                            AccessKey.PAGE_REVIEWS_EXPORT
                        ]}
                        authenticated
                    >
                        <ReviewAnalyticsRoute />
                    </AppRoute>
                ),
                children: [
                    {
                        path: ':ean',
                        element: (
                            <AppRoute requireAccessKey={AccessKey.PAGE_REVIEWS_PRODUCTS} authenticated>
                                <ProductReviewsController />
                            </AppRoute>
                        )
                    },
                    {
                        index: true,
                        element: (
                            <AppRoute
                                requireAccessKey={[
                                    AccessKey.PAGE_REVIEWS_PRODUCTS,
                                    AccessKey.PAGE_REVIEWS_FEED,
                                    AccessKey.PAGE_REVIEWS_SETTINGS,
                                    AccessKey.PAGE_REVIEWS_EXPORT
                                ]}
                                authenticated
                            >
                                <ReviewDashboardController />
                            </AppRoute>
                        )
                    }
                ]
            },
            {
                path: PRODUCTS_PATH,
                element: (
                    <AppRoute authenticated requireAccessKey={AccessKey.PAGE_PRODUCTS}>
                        <ProductsController />
                    </AppRoute>
                )
            },
            {path: QR_CODES_PATH, element: <Navigate to={PRODUCTS_ROUTE} replace />},
            {
                path: SEARCH_TRENDS_PATH,
                element: (
                    <AppRoute requireAccessKey={AccessKey.PAGE_SEARCH_TERMS} authenticated>
                        <Navigate to={SEARCH_VOLUMES_ROUTE} />
                    </AppRoute>
                )
            },
            {
                path: SEARCH_VOLUMES_PATH,
                element: (
                    <AppRoute requireAccessKey={AccessKey.PAGE_SEARCH_TERMS} authenticated>
                        <SearchVolumeController />
                    </AppRoute>
                )
            },
            {
                path: SALES_DASHBOARD_PATH,
                element: (
                    <AppRoute
                        requireAccessKey={AccessKey.PAGE_SALES_DASHBOARD}
                        authenticated
                        adminOnly
                        fallbackRoute={SALES_DASHBOARD_V2_ROUTE}
                    >
                        <SalesDashboardController />
                    </AppRoute>
                )
            },
            {
                path: SALES_DASHBOARD_V2_PATH,
                element: (
                    <AppRoute requireAccessKey={AccessKey.PAGE_SALES_DASHBOARD} authenticated>
                        <SalesDashboardControllerV2 />
                    </AppRoute>
                )
            },
            {
                path: SEARCH_RANKINGS_PATH,
                element: (
                    <AppRoute requireAccessKey={AccessKey.PAGE_SEARCH_RANKINGS} authenticated>
                        <SearchRankingsController />
                    </AppRoute>
                )
            },
            {
                path: SETTINGS_PATH,
                element: (
                    <AppRoute authenticated>
                        <Settings />
                    </AppRoute>
                ),
                children: [
                    {path: COMMON_SETTINGS_PATH, element: <CommonSettingsController />},
                    {
                        path: ACCESS_SETTINGS_PATH,
                        element: (
                            <AppRoute
                                requireAccessKey={AccessKey.PAGE_ACCOUNT_USERS}
                                authenticated
                                fallbackRoute={COMMON_SETTINGS_ROUTE}
                            >
                                <AccountUsersController />
                            </AppRoute>
                        )
                    },
                    {path: SECURITY_SETTINGS_PATH, element: <PasswordSettingsController />},
                    {
                        path: API_SETTINGS_PATH,
                        element: (
                            <AppRoute
                                requireAccessKey={AccessKey.PAGE_SETTINGS_API}
                                fallbackRoute={COMMON_SETTINGS_ROUTE}
                            >
                                <ApiSettingsController />
                            </AppRoute>
                        )
                    },
                    {
                        path: INVOICE_SETTINGS_PATH,
                        element: (
                            <AppRoute
                                requireAccessKey={AccessKey.PAGE_SETTINGS_INVOICE}
                                fallbackRoute={COMMON_SETTINGS_ROUTE}
                            >
                                <InvoiceSettingsController />
                            </AppRoute>
                        )
                    },
                    {
                        path: PRODUCT_SETTINGS_PATH,
                        element: (
                            <AppRoute
                                requireAccessKey={AccessKey.PAGE_SETTINGS_PRODUCTS}
                                fallbackRoute={COMMON_SETTINGS_ROUTE}
                            >
                                <ProductsMovedController />
                            </AppRoute>
                        )
                    },
                    {
                        path: SUBSCRIPTION_PATH,
                        element: (
                            <AppRoute
                                requireAccessKey={AccessKey.PAGE_SETTINGS_SUBSCRIPTION}
                                fallbackRoute={COMMON_SETTINGS_ROUTE}
                            >
                                <SubscriptionSettingsController />
                            </AppRoute>
                        )
                    },
                    {
                        path: EXPORT_PATH,
                        element: (
                            <AppRoute
                                requireAccessKey={AccessKey.PAGE_SETTINGS_EXPORT}
                                fallbackRoute={COMMON_SETTINGS_ROUTE}
                            >
                                <ExportInvoicesController />
                            </AppRoute>
                        )
                    },
                    {
                        path: EMAIL_SETTINGS_PATH,
                        element: (
                            <AppRoute
                                requireAccessKey={AccessKey.PAGE_SETTINGS_EMAILS}
                                fallbackRoute={COMMON_SETTINGS_ROUTE}
                            >
                                <EmailSettingsController />
                            </AppRoute>
                        )
                    },
                    {index: true, element: <Navigate to={COMMON_SETTINGS_ROUTE} />}
                ]
            },
            {
                path: 'account-instellen',
                element: (
                    <AppRoute authenticated>
                        <CreateAccountController />
                    </AppRoute>
                )
            },
            {
                path: CREDIT_DATA_PATH,
                element: (
                    <AppRoute requireAccessKey={AccessKey.PAGE_ORDERS} authenticated>
                        <CreditInvoiceDataController />
                    </AppRoute>
                )
            },
            {
                path: IDEAL_PATH,
                element: (
                    <AppRoute requireAccessKey={AccessKey.PAGE_SETTINGS_SUBSCRIPTION} authenticated>
                        <IdealController />
                    </AppRoute>
                )
            },
            {
                path: IDEAL_COMPLETE_PATH,
                element: (
                    <AppRoute requireAccessKey={AccessKey.PAGE_SETTINGS_SUBSCRIPTION} authenticated>
                        <ConfirmIdeal />
                    </AppRoute>
                )
            },
            {
                path: EMAILS_PATH,
                element: (
                    <AppRoute requireAccessKey={AccessKey.PAGE_EMAIL} authenticated>
                        <EmailFlowController />
                    </AppRoute>
                )
            },
            {
                path: EMAIL_SEQUENCE_PATH,
                element: (
                    <AppRoute requireAccessKey={AccessKey.PAGE_EMAIL} authenticated>
                        <Navigate to={EMAILS_ROUTE} replace />
                    </AppRoute>
                )
            },
            {
                path: EMAIL_TEMPLATE_PATH,
                element: (
                    <AppRoute requireAccessKey={AccessKey.PAGE_EMAIL} authenticated>
                        <TemplateEditorController />
                    </AppRoute>
                )
            },
            {
                path: RETURNS_PATH,
                element: (
                    <AppRoute requireAccessKey={AccessKey.PAGE_RETURNS} authenticated>
                        <ReturnsController />
                    </AppRoute>
                )
            },
            {
                path: VAT_DECLARATION_PATH,
                element: (
                    <AppRoute requireAccessKey={AccessKey.PAGE_VAT_DECLARATION} authenticated>
                        <VatDeclarationController />
                    </AppRoute>
                )
            },
            {
                path: UNSUBSCRIBE_MONTHLY_PATH,
                element: (
                    <AppRoute>
                        <UnsubscribeMonthly />
                    </AppRoute>
                )
            },
            {
                path: ADMIN_PATH,
                element: (
                    <AppRoute authenticated>
                        <AdminController />
                    </AppRoute>
                ),
                children: [
                    {path: ADMIN_USER_PATH, element: <UserDetailsController />},
                    {path: ADMIN_ACCOUNT_PATH, element: <AccountDetailsController />},
                    {path: ADMIN_THREADS_PATH, element: <ThreadsController />},
                    {path: ADMIN_USERS_PATH, element: <AdminUsersController />},
                    {path: ADMIN_SERVICES_PATH, element: <SystemServicesController />},
                    {path: ADMIN_IMPORT_PATH, element: <HistoryImportController />},
                    {path: ADMIN_EMAILS_PATH, element: <AdminEmailSequenceController />},
                    {path: ADMIN_PAYMENTS_PATH, element: <FailedPaymentsController />},
                    {path: ADMIN_USERS_SELECT_PATH, element: <UserSelectionController />},
                    {path: ADMIN_DEFAULT_TEMPLATES_PATH, element: <DefaultEmailsController />},
                    {path: ADMIN_TEMPLATE_EDITOR_PATH, element: <DefaultEmailEditorController />},
                    {path: ADMIN_PLATFORM_USAGE_PATH, element: <PlatformUsageController />},
                    {element: <AdminNavigation />, index: true}
                ]
            },
            {path: OAUTH_PATH, element: <BolOauthController />},
            {path: OAUTH_INSIGHTS_PATH, element: <BolOauthController />},
            {
                path: OAUTH_AUTHORIZE_PATH,
                element: (
                    <AppRoute>
                        <OAuthAuthorizeController />
                    </AppRoute>
                )
            },
            {
                path: REFER_A_FRIEND_PATH,
                element: (
                    <AppRoute authenticated>
                        <ReferAFriendController />
                    </AppRoute>
                )
            },
            {
                path: MOBILE_APP_PATH,
                element: (
                    <AppRoute authenticated>
                        <MobileAppController />
                    </AppRoute>
                )
            },
            {
                path: CHROME_EXTENSION_PATH,
                element: (
                    <AppRoute authenticated>
                        <ChromeExtensionController />
                    </AppRoute>
                )
            },
            {
                path: MCP_SERVER_PATH,
                element: (
                    <AppRoute authenticated>
                        <McpServerController />
                    </AppRoute>
                )
            },
            {
                path: NOTIFICATIONS_PATH,
                element: (
                    <AppRoute requireAccessKey={AccessKey.PAGE_NOTIFICATIONS} authenticated>
                        <NotificationsPageController />
                    </AppRoute>
                )
            },
            {
                path: DASHBOARD_PATH,
                element: (
                    <AppRoute requireAccessKey={AccessKey.PAGE_DASHBOARD} authenticated>
                        <DashboardPageController />
                    </AppRoute>
                )
            },
            {path: '*', element: <Navigate to={DASHBOARD_ROUTE} />}
        ]
    }
];
